import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Switch } from "../../../../form/switch";
import { Textarea } from "../../../../form/textarea";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { getGoogleAccountToken } from "../../../../auth/method";
import { AuthenticateSelect } from "../../../../auth/select";
import { IoWarningOutline } from "react-icons/io5";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { webURL } from "../../../../../services/API";
import { DateInput } from "../../../../form/date";

type GoogleCalendarProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function GoogleCalendar({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: GoogleCalendarProps) {
  // Request types for Google Calendar Actions
  const [requests, setRequests] = React.useState<selectDataType[]>([
    { id: 1, name: "Create event", refId: "create_event", isActive: true },
    { id: 2, name: "Get event", refId: "get_event" },
    { id: 3, name: "Get many events", refId: "get_many_events" },
    { id: 4, name: "Update event", refId: "update_event" },
    { id: 5, name: "Delete event", refId: "delete_event" },
    { id: 6, name: "Get availability", refId: "get_availability" },
    { id: 7, name: "Get calendar list", refId: "get_calendar_list" },
    { id: 8, name: "Get all events", refId: "get_all_events" },
    { id: 9, name: "Watch events", refId: "watch_events" },
    { id: 10, name: "Fetch changes", refId: "fetch_changes" },
    { id: 11, name: "Let AI Agent Decide", refId: "agent_decide" }
  ]);

  // https://calendar.google.com/calendar/u/0/r/eventedit/MWhsYjd1c2xldm8zOWtlYTk0Z2pqNnI2aWQgeW91c2VmZXNsYW0yMTRAbQ

  // Order by options for Get Many Events
  const [orderByOptions] = React.useState<selectDataType[]>([
    { id: 1, name: "Start Time", refId: "startTime", isActive: true },
    { id: 2, name: "Updated", refId: "updated" },
  ]);

  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Initialize minimize and settings handlers
  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  // Initialize default values on first render
  React.useEffect(() => {
    if (!data.value) {
      // Set default request based on category
      if (data.currentCategory === "Actions") {
        updateNodeValue(id, setNodes, {
          request: "create_event",
          calendars: [],
          calendarId: "primary",
          timezone: "UTC",
          orderBy: "startTime",
        });
      } else {
        updateNodeValue(id, setNodes, {
          request: requests[0].refId,
          calendars: [],
          calendarId: "primary",
          timezone: "UTC",
          orderBy: "startTime",
        });
      }
    } else {
      // Restore active request from saved state
      setRequests((requests) =>
        requests.map((request) => ({
          ...request,
          isActive: request.refId === data.value.request,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthenticate = async (type: "add" | "remove", authData: any) => {
    if (type === "add") {
      const token = await getGoogleAccountToken(authData, id, setNodes);

      // Fetch calendar list after authentication
      try {
        // /blocks/google_calendar_list
        const response = await axios.get(
          `${webURL}/blocks/google_calendar_list`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          let calendars = response.data.calendars.map(
            (cal: any, index: number) => ({
              id: cal.id,
              name: cal.summary || cal.id,
              isActive: true,
            })
          );

          // Only show calendars with @gmail.com in their id
          const gmailCalendars = calendars.filter((cal: any) =>
            cal.id.includes("@gmail.com")
          );
          calendars = gmailCalendars.length > 0 ? [gmailCalendars[0]] : [];
            updateNodeValue(id, setNodes, {
              connection: authData,
              calendars,
              calendarId: calendars[0]?.id || 'primary',
            });
         }
      } catch (error) {
        updateNodeValue(id, setNodes, { connection: authData });
      }
    } else if (type === "remove") {
      updateNodeValue(id, setNodes, { connection: null, calendars: [] });
    }
  };

  // Handle request and calendar selection
  const handleSelectRequests = (selectId: number | string) => {
    const refId = requests.find((obj) => obj.id === selectId)?.refId;

    setRequests((requests) =>
      requests.map((request) => {
        if (request.id === selectId) return { ...request, isActive: true };
        else return { ...request, isActive: false };
      })
    );

    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              value: { ...node.data.value, request: refId },
            },
          };
        } else return node;
      })
    );
  };
  const handleSelectCalendars = (selectId: number | string) => {
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          const updatedCalendars = node.data.value.calendars.map(
            (item: any) => {
              const isActive = item.id === selectId;

              return {
                ...item,
                isActive,
              };
            }
          );

          return {
            ...node,
            data: {
              ...node.data,
              value: {
                ...node.data.value,
                calendars: updatedCalendars,
                calendarId: selectId, // Store the selected calendar ID directly
              },
            },
          };
        } else return node;
      })
    );
  };
  // Handle input changes
  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    keyRef?: string
  ) => {
    const value = e.target.value;
    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
  };

  // Handle date input changes (DateInput passes value directly, not event)
  const handleChangeDateInput = (value: string, keyRef?: string) => {
    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
  };

  // Handle order by selection for Get Many Events
  const handleSelectOrderBy = (selectId: number) => {
    const refId = orderByOptions.find((obj) => obj.id === selectId)?.refId;
    updateNodeValue(id, setNodes, { orderBy: refId });
  };

  // ---
  const isWarning = !data.value?.connection;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : null}
        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "435px" }}>
            <Styled>
              <AuthenticateSelect
                type="google"
                defaultId={data.value?.connection?.id}
                handleAuthenticate={handleAuthenticate}
                background={data.background}
              />

              {/* Action Button */}
              <div className="action" />

              {/* Request Type Selector */}
              <Select
                title="Action"
                data={requests}
                handleSelectItem={(id) => handleSelectRequests(id)}
                isSmall={true}
              />

              {/* Calendar Selection - Show dropdown if calendars available, otherwise text input */}
              {data.value?.calendars?.length > 0 ? (
                <Select
                  title="Calendar"
                  data={data.value.calendars}
                  handleSelectItem={(id) => handleSelectCalendars(id)}
                  isSmall={true}
                />
              ) : (
                <TextInput
                  keyRef="calendarId"
                  value={data.value?.calendarId || "primary"}
                  title="Calendar ID"
                  placeholder="primary"
                  handleChange={handleChangeInput}
                />
              )}

              {/* CREATE EVENT Fields */}
              {data.value?.request === "create_event" ? (
                <>
                  <TextInput
                    keyRef="summary"
                    value={data.value?.summary}
                    title="Event Title"
                    placeholder="Meeting with team"
                    handleChange={handleChangeInput}
                  />
                  <Textarea
                    value={data.value?.description}
                    placeholder="Event description"
                    handleChange={(e) =>
                      updateNodeValue(id, setNodes, {
                        description: e.target.value,
                      })
                    }
                  />
                  <DateInput
                    keyRef="start_datetime"
                    value={data.value?.start_datetime}
                    title="Start Date"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <DateInput
                    keyRef="end_datetime"
                    value={data.value?.end_datetime}
                    title="End Date"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <TextInput
                    keyRef="attendees"
                    value={data.value?.attendees}
                    title="Attendees"
                    placeholder="user1@example.com, user2@example.com"
                    handleChange={handleChangeInput}
                  />
                  <Switch
                    title="All Day Event"
                    value={!!data.value?.isAllDay}
                    setValue={() =>
                      updateNodeValue(id, setNodes, {
                        isAllDay: !data.value?.isAllDay,
                      })
                    }
                  />
                </>
              ) : null}

              {/* GET EVENT Fields */}
              {data.value?.request === "get_event" ? (
                <TextInput
                  keyRef="eventId"
                  value={data.value?.eventId}
                  title="Event ID"
                  placeholder="abc123xyz"
                  handleChange={handleChangeInput}
                />
              ) : null}

              {/* GET MANY EVENTS Fields */}
              {data.value?.request === "get_many_events" ? (
                <>
                  <DateInput
                    keyRef="timeMin"
                    value={data.value?.timeMin || "2024-12-01T00:00:00Z"}
                    title="Start Time"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <DateInput
                    keyRef="timeMax"
                    value={data.value?.timeMax || "2025-12-31T23:59:59Z"}
                    title="End Time"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <TextInput
                    keyRef="maxResults"
                    value={data.value?.maxResults || "10"}
                    title="Limit"
                    placeholder="10"
                    handleChange={handleChangeDateInput}
                  />
                  <TextInput
                    keyRef="keywords"
                    value={data.value?.keywords}
                    title="Keywords"
                    placeholder="Search Keywords (optional)"
                    handleChange={handleChangeInput}
                  />
                  <Select
                    title="Order By"
                    data={orderByOptions.map((opt) => ({
                      ...opt,
                      isActive:
                        opt.refId === (data.value?.orderBy || "startTime"),
                    }))}
                    handleSelectItem={handleSelectOrderBy}
                    isSmall={true}
                  />
                </>
              ) : null}

              {/* UPDATE EVENT Fields */}
              {data.value?.request === "update_event" ? (
                <>
                  <TextInput
                    keyRef="eventId"
                    value={data.value?.eventId}
                    title="Event ID"
                    placeholder="abc123xyz"
                    handleChange={handleChangeInput}
                  />
                  <TextInput
                    keyRef="summary"
                    value={data.value?.summary}
                    title="Event Title"
                    placeholder="Updated Meeting"
                    handleChange={handleChangeInput}
                  />
                  <Textarea
                    value={data.value?.description}
                    placeholder="Updated description"
                    handleChange={(e) =>
                      updateNodeValue(id, setNodes, {
                        description: e.target.value,
                      })
                    }
                  />
                  <DateInput
                    keyRef="start_datetime"
                    value={data.value?.start_datetime}
                    title="Start Date"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <DateInput
                    keyRef="end_datetime"
                    value={data.value?.end_datetime}
                    title="End Date"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <TextInput
                    keyRef="attendees"
                    value={data.value?.attendees}
                    title="Attendees"
                    placeholder="user1@example.com, user2@example.com"
                    handleChange={handleChangeInput}
                  />
                  {/* <TextInput
                     keyRef="timezone"
                     value={data.value?.timezone || "UTC"}
                     title="Timezone"
                     placeholder="UTC"
                     handleChange={handleChangeInput}
                     /> */}
                </>
              ) : null}

              {/* DELETE EVENT Fields */}
              {data.value?.request === "delete_event" ? (
                <>
                  <TextInput
                    keyRef="eventId"
                    value={data.value?.eventId}
                    title="Event ID"
                    placeholder="abc123xyz"
                    handleChange={handleChangeInput}
                  />
                  <p
                    style={{
                      color: "#ff6b6b",
                      fontSize: "13px",
                      marginTop: "20px",
                    }}>
                    ⚠️ Warning: This action will permanently delete the event.
                  </p>
                </>
              ) : null}

              {/* GET AVAILABILITY Fields */}
              {data.value?.request === "get_availability" ? (
                <>
                  <DateInput
                    keyRef="timeMin"
                    value={data.value?.timeMin || "2024-12-01T00:00:00Z"}
                    title="Start Time"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                  <DateInput
                    keyRef="timeMax"
                    value={data.value?.timeMax || "2025-12-01T23:59:59Z"}
                    title="End Time"
                    isWithTime
                    handleChange={handleChangeDateInput}
                  />
                </>
              ) : null}

              {/* WATCH EVENTS Fields */}
              {data.value?.request === "watch_events" ? (
                <TextInput
                  keyRef="ttl"
                  value={data.value?.ttl}
                  title="TTL"
                  placeholder="Time to Live in seconds - optional"
                  handleChange={handleChangeInput}
                />
              ) : null}

              {/* FETCH CHANGES Fields */}
              {data.value?.request === "fetch_changes" ? (
                <TextInput
                  keyRef="syncToken"
                  value={data.value?.syncToken}
                  title="Sync Token"
                  placeholder="From watch_events or prev fetch"
                  handleChange={handleChangeInput}
                />
              ) : null}
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}